/**
 * Canonical skill options for pickers (#2905).
 *
 * HR lists every catalog row (with type chips). The service required-skills
 * picker must show the same *active* catalog, minus types that cannot satisfy
 * a requirement and minus duplicate names created when the taxonomy import
 * (#1140) landed next to hand-created rows.
 *
 * `interest` is a proficiency, not a skill type — it never meets a minimum
 * (see skill-matching.ts). If it appears as a `type` it is still excluded.
 */

export interface SkillOptionInput {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  sortOrder: number;
  description?: string | null;
}

/** Types a service may require. Equipment is a mechanic capability, not a lift. */
const REQUIRED_SKILL_TYPE_RANK: Record<string, number> = {
  service_category: 0,
  brand_certification: 1,
  equipment: 2,
};

export function normalizeSkillName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('fi');
}

export function isRequiredSkillType(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(REQUIRED_SKILL_TYPE_RANK, type);
}

export function skillTypeGroupKey(type: string): string {
  switch (type) {
    case 'service_category':
      return 'skills.typeServiceCategory';
    case 'brand_certification':
      return 'skills.typeBrandCertification';
    case 'equipment':
      return 'skills.typeEquipment';
    default:
      return 'skills.type';
  }
}

function typeRank(type: string): number {
  return REQUIRED_SKILL_TYPE_RANK[type] ?? 99;
}

function hasDescription(skill: SkillOptionInput): boolean {
  return Boolean(skill.description && skill.description.trim());
}

/** True when `candidate` should replace `current` for the same normalised name. */
function preferSkill<T extends SkillOptionInput>(candidate: T, current: T): boolean {
  const rankDiff = typeRank(candidate.type) - typeRank(current.type);
  if (rankDiff !== 0) return rankDiff < 0;
  if (candidate.sortOrder !== current.sortOrder) {
    return candidate.sortOrder < current.sortOrder;
  }
  const descDiff = Number(hasDescription(candidate)) - Number(hasDescription(current));
  if (descDiff !== 0) return descDiff > 0;
  return candidate.id < current.id;
}

/**
 * Active required-skill rows, one per normalised name, grouped-ready
 * (type rank then Finnish name).
 */
export function toRequiredSkillOptions<T extends SkillOptionInput>(
  skills: readonly T[],
): T[] {
  const byName = new Map<string, T>();
  for (const skill of skills) {
    if (!skill.isActive) continue;
    if (!isRequiredSkillType(skill.type)) continue;
    const key = normalizeSkillName(skill.name);
    if (!key) continue;
    const existing = byName.get(key);
    if (!existing || preferSkill(skill, existing)) {
      byName.set(key, skill);
    }
  }
  return [...byName.values()].sort((a, b) => {
    const rankDiff = typeRank(a.type) - typeRank(b.type);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name, 'fi');
  });
}

/** First-seen id wins. Guards against a broken list cursor repeating rows. */
export function uniqueById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    unique.push(item);
  }
  return unique;
}
